import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import '@/App.css';

import LandingPage from '@/pages/LandingPage';
import { ChangePasswordPage, LoginPage, RegisterPage } from '@/pages/AuthPages';
import DashboardPage from '@/pages/DashboardPage';
import CreateAdPage from '@/pages/CreateAdPage';
import WalletPage from '@/pages/WalletPage';
import AdminPage from '@/pages/AdminPage';
import BusinessProfilePage from '@/pages/BusinessProfilePage';
import CampaignDetailPage from '@/pages/CampaignDetailPage';
import PricingPage from '@/pages/PricingPage';
import Layout from '@/components/Layout';

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dot-grid">
        <div className="flex gap-1">
          <span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (adminOnly && !['admin', 'super_admin'].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dot-grid">
        <div className="flex gap-1">
          <span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" />
        </div>
      </div>
    );
  }
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/change-password" element={user ? <ChangePasswordPage /> : <Navigate to="/login" replace />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/create-ad" element={<ProtectedRoute><Layout><CreateAdPage /></Layout></ProtectedRoute>} />
      <Route path="/create-ad/:campaignId" element={<ProtectedRoute><Layout><CreateAdPage /></Layout></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><Layout><WalletPage /></Layout></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><Layout><PricingPage /></Layout></ProtectedRoute>} />
      <Route path="/campaigns/:campaignId" element={<ProtectedRoute><Layout><CampaignDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/business-profile" element={<ProtectedRoute><Layout><BusinessProfilePage /></Layout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><Layout><AdminPage /></Layout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
